import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { getCampaignContactEvents } from 'API/linkedin-funnel';
import AdminSubpageHeader from 'features/admin/admin-subpage-header';
import { useUserData } from 'hooks/use-user-data';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { Roles } from '../enums';

type CampaignContactEvent = {
  _id: string;
  campaignId: string;
  contactId: string;
  accountId?: string;
  stepId?: string;
  stepIndex?: number;
  stepType?: string;
  eventType: string;
  reason?: string;
  details?: Record<string, unknown>;
  occurredAt: string;
  createdAt: string;
};

type CampaignContactEventsResponse = {
  campaignId: string;
  contactId: string;
  count: number;
  events: CampaignContactEvent[];
};

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const AdminCampaignContactTimelinePage: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { role } = useUserData();
  const [campaignId, setCampaignId] = useState('');
  const [contactId, setContactId] = useState('');
  const [eventLimit, setEventLimit] = useState(200);
  const [eventsResponse, setEventsResponse] = useState<CampaignContactEventsResponse | null>(null);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CampaignContactEvent | null>(null);
  const [copyToast, setCopyToast] = useState<string | null>(null);

  const idsLookValid = useMemo(() => {
    return UUID_REGEX.test(campaignId.trim()) && UUID_REGEX.test(contactId.trim());
  }, [campaignId, contactId]);

  const eventTypeCounts = useMemo(() => {
    const entries = eventsResponse?.events || [];
    const map = new Map<string, number>();
    entries.forEach((eventItem) => {
      const key = String(eventItem.eventType || 'unknown');
      map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [eventsResponse]);

  const fetchCampaignContactEvents = async () => {
    const trimmedCampaignId = campaignId.trim();
    const trimmedContactId = contactId.trim();

    if (!UUID_REGEX.test(trimmedCampaignId) || !UUID_REGEX.test(trimmedContactId)) {
      setEventsError(t('adminTimelineUuidError', 'Please enter valid UUIDs for campaign ID and contact ID.'));
      setEventsResponse(null);
      return;
    }

    setLoadingEvents(true);
    setEventsError(null);
    try {
      const data = await getCampaignContactEvents(trimmedCampaignId, trimmedContactId, eventLimit);
      setEventsResponse((data || null) as CampaignContactEventsResponse | null);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      setEventsError(err?.response?.data?.message || err?.message || 'Could not load campaign contact events');
      setEventsResponse(null);
    } finally {
      setLoadingEvents(false);
    }
  };

  const clearEventsView = () => {
    setEventsResponse(null);
    setEventsError(null);
    setSelectedEvent(null);
  };

  const copyText = async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyToast(successMessage);
    } catch {
      setCopyToast(t('copyFailed', 'Copy failed'));
    }
  };

  if (role !== Roles.OWNER) {
    return (
      <div className={classes.page}>
        <Typography>{t('noPermission', 'You do not have permission to access this page.')}</Typography>
      </div>
    );
  }

  return (
    <div className={classes.page}>
      <AdminSubpageHeader title={t('adminNavCampaignTimeline')} />
      <Paper className={classes.card} elevation={0}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1} flexWrap="wrap" gap={1}>
          <Typography className={classes.title}>{t('adminCampaignTimelinePageTitle', 'Campaign Contact Event Timeline')}</Typography>
          <Chip
            size="small"
            color={eventsResponse?.events?.length ? 'success' : 'default'}
            label={eventsResponse ? `${eventsResponse.events.length} events` : t('adminTimelineNoData', 'No data loaded')}
          />
        </Stack>

        <Typography className={classes.hint}>
          {t(
            'adminCampaignTimelineHint',
            'Debug view for one campaign-contact pair. Enter IDs, load the timeline, and inspect deferrals, replies, cooldown hits, and finish reasons in execution order.',
          )}
        </Typography>

        <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} mt={1.5}>
          <TextField
            size="small"
            fullWidth
            label={t('campaignId', 'Campaign ID')}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            value={campaignId}
            onChange={(event) => setCampaignId(event.target.value)}
            error={campaignId.trim().length > 0 && !UUID_REGEX.test(campaignId.trim())}
          />
          <TextField
            size="small"
            fullWidth
            label={t('contactId', 'Contact ID')}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            value={contactId}
            onChange={(event) => setContactId(event.target.value)}
            error={contactId.trim().length > 0 && !UUID_REGEX.test(contactId.trim())}
          />
          <TextField
            select
            size="small"
            label={t('limit', 'Limit')}
            value={eventLimit}
            onChange={(event) => setEventLimit(Number(event.target.value))}
            sx={{ minWidth: 110 }}
          >
            {[50, 100, 200, 300, 500].map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Stack direction="row" gap={1} mt={1.5}>
          <Button variant="contained" onClick={fetchCampaignContactEvents} disabled={loadingEvents || !idsLookValid}>
            {loadingEvents ? t('loading', 'Loading...') : t('adminLoadTimeline', 'Load timeline')}
          </Button>
          <Button variant="outlined" onClick={clearEventsView} disabled={loadingEvents}>
            {t('clear', 'Clear')}
          </Button>
        </Stack>

        {loadingEvents ? <LinearProgress sx={{ mt: 1.5 }} /> : null}
        {eventsError ? (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {eventsError}
          </Alert>
        ) : null}

        {eventsResponse ? (
          <Box mt={1.5}>
            <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
              <Chip label={`Campaign: ${eventsResponse.campaignId}`} />
              <Chip label={`Contact: ${eventsResponse.contactId}`} />
              <Chip label={`Total: ${eventsResponse.count}`} />
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  copyText(
                    JSON.stringify({
                      campaignId: eventsResponse.campaignId,
                      contactId: eventsResponse.contactId,
                    }),
                    t('idsCopied', 'IDs copied'),
                  )
                }
              >
                {t('copyIds', 'Copy IDs')}
              </Button>
            </Stack>

            {eventTypeCounts.length ? (
              <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap mt={1}>
                {eventTypeCounts.map(([eventType, count]) => (
                  <Chip key={eventType} size="small" label={`${eventType}: ${count}`} />
                ))}
              </Stack>
            ) : null}

            <Divider sx={{ my: 1.5 }} />

            <Stack gap={1}>
              {(eventsResponse.events || []).map((eventItem) => (
                <Paper key={eventItem._id} variant="outlined" className={classes.eventRow}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    gap={0.75}
                  >
                    <Stack direction="row" alignItems="center" gap={0.75}>
                      <Chip size="small" label={eventItem.eventType} />
                      <Typography className={classes.eventTime}>{new Date(eventItem.occurredAt).toLocaleString()}</Typography>
                    </Stack>
                    <Typography className={classes.eventMeta}>
                      step {eventItem.stepIndex ?? '-'} | {eventItem.stepType || '-'} | account {eventItem.accountId || '-'}
                    </Typography>
                  </Stack>

                  {eventItem.reason ? <Typography className={classes.eventReason}>Reason: {eventItem.reason}</Typography> : null}
                  {eventItem.details ? (
                    <Typography className={classes.eventDetails}>{JSON.stringify(eventItem.details)}</Typography>
                  ) : null}

                  <Stack direction="row" gap={1} mt={1}>
                    <Button size="small" variant="outlined" onClick={() => setSelectedEvent(eventItem)}>
                      {t('viewDetails', 'View details')}
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => copyText(eventItem._id, t('eventIdCopied', 'Event ID copied'))}>
                      {t('copyEventId', 'Copy event ID')}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => copyText(JSON.stringify(eventItem, null, 2), t('eventJsonCopied', 'Event JSON copied'))}
                    >
                      {t('copyEventJson', 'Copy event JSON')}
                    </Button>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Box>
        ) : null}
      </Paper>

      <Drawer anchor="right" open={Boolean(selectedEvent)} onClose={() => setSelectedEvent(null)}>
        <Box className={classes.eventDrawer}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography className={classes.title}>{t('eventDetails', 'Event details')}</Typography>
            <Button size="small" onClick={() => setSelectedEvent(null)}>
              {t('close', 'Close')}
            </Button>
          </Stack>

          {selectedEvent ? (
            <Stack gap={1}>
              <Chip size="small" label={selectedEvent.eventType} />
              <Typography className={classes.eventMeta}>{new Date(selectedEvent.occurredAt).toLocaleString()}</Typography>
              <Typography className={classes.eventMeta}>campaign: {selectedEvent.campaignId}</Typography>
              <Typography className={classes.eventMeta}>contact: {selectedEvent.contactId}</Typography>
              <Typography className={classes.eventMeta}>account: {selectedEvent.accountId || '-'}</Typography>
              <Typography className={classes.eventMeta}>
                step: {selectedEvent.stepIndex ?? '-'} | {selectedEvent.stepType || '-'} | stepId {selectedEvent.stepId || '-'}
              </Typography>
              <Typography className={classes.eventReason}>Reason: {selectedEvent.reason || '-'}</Typography>

              <Divider />
              <Typography className={classes.listTitle}>{t('detailsJson', 'Details JSON')}</Typography>
              <Box className={classes.eventJsonBox}>
                <pre className={classes.eventJsonPre}>{JSON.stringify(selectedEvent.details || {}, null, 2)}</pre>
              </Box>

              <Stack direction="row" gap={1}>
                <Button size="small" variant="outlined" onClick={() => copyText(selectedEvent._id, t('eventIdCopied', 'Event ID copied'))}>
                  {t('copyEventId', 'Copy event ID')}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => copyText(JSON.stringify(selectedEvent, null, 2), t('fullJsonCopied', 'Full JSON copied'))}
                >
                  {t('copyFullJson', 'Copy full JSON')}
                </Button>
              </Stack>
            </Stack>
          ) : null}
        </Box>
      </Drawer>

      <Snackbar open={Boolean(copyToast)} autoHideDuration={1800} onClose={() => setCopyToast(null)} message={copyToast || ''} />
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  page: {
    padding: '20px 16px 16px 48px',
    [theme.breakpoints.down('lg')]: {
      padding: '20px 8px 8px 8px',
    },
  },
  card: {
    border: `1px solid ${theme.palette.greys.light}`,
    borderRadius: 8,
    padding: 12,
    maxWidth: 900,
  },
  title: {
    fontWeight: 600,
  },
  hint: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(12),
  },
  listTitle: {
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(13),
  },
  eventRow: {
    padding: '8px 10px',
  },
  eventTime: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
  },
  eventMeta: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
    fontFamily: 'monospace',
  },
  eventReason: {
    marginTop: 6,
    fontSize: theme.typography.pxToRem(12),
  },
  eventDetails: {
    marginTop: 4,
    fontSize: theme.typography.pxToRem(11),
    fontFamily: 'monospace',
    color: theme.palette.text.secondary,
    wordBreak: 'break-word',
  },
  eventDrawer: {
    width: 520,
    maxWidth: '92vw',
    padding: 16,
  },
  eventJsonBox: {
    border: `1px solid ${theme.palette.greys.light}`,
    borderRadius: 6,
    background: theme.palette.greys.lighter,
    padding: 8,
    maxHeight: 320,
    overflow: 'auto',
  },
  eventJsonPre: {
    margin: 0,
    fontFamily: 'monospace',
    fontSize: theme.typography.pxToRem(12),
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
}));

export default AdminCampaignContactTimelinePage;
